import {
  BlockVariant,
  ButtonBlockProps,
  CommentBlockProps,
  DynamicBlock,
  DynamicBlocks,
  InputTextareaBlockProps,
} from '@/components/block-builder/block-builder.types';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthorizationService } from '@/services/authorization.service';
import { UserRole } from '@/types/user.types';
interface GetPublicationBlocksProps {
  publish: () => void;
  cancel: () => void;
  loadingValidation: BehaviorSubject<boolean>;
  previousComment?: string;
  previousCommentDate?: string;
  comment?: BehaviorSubject<string>;
  onCommentChange?: (val: string) => void;
  error?: BehaviorSubject<string | null>;
}

@Injectable({
  providedIn: 'root',
})
export class ClarificationWorkflowBlocksService {
  translate = inject(TranslateService);
  authService = inject(AuthorizationService);

  getPublicationBlocks = ({
    publish,
    cancel,
    loadingValidation,
    previousComment,
    previousCommentDate,
    comment,
    onCommentChange,
    error,
  }: GetPublicationBlocksProps): DynamicBlocks[] => {
    const publishButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.publish',
        onClick: () => publish(),
        loading: loadingValidation,
        severity: 'success',
      },
    };

    const cancelButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.cancel',
        onClick: () => cancel(),
        severity: 'secondary',
      },
    };

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: previousComment || '',
        date: previousCommentDate || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const textareaBlock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'clarification.form.comment',
        placeholder: 'clarification.form.comment_placeholder',
        value: comment?.getValue() || '',
        onChange: (value: string) => onCommentChange?.(value),
        rows: 5,
        error,
      },
    };

    const finalBlocks: DynamicBlocks = {};

    if (previousComment) finalBlocks['0'] = [commentBlock];
    finalBlocks['1'] = [publishButton, cancelButton];

    return [finalBlocks];
  };

  getCpmpFiltrationBlocks = ({
    validate,
    reject,
    cancel,
    onCommentChange,
    comment,
    loadingValidation,
    error,
    previousComment,
    previousCommentDate,
  }: {
    validate: () => void;
    reject: () => void;
    cancel: () => void;
    onCommentChange: (comment: string) => void;
    comment: BehaviorSubject<string>;
    loadingValidation: BehaviorSubject<boolean>;
    error?: BehaviorSubject<string | null>;
    previousComment?: string;
    previousCommentDate?: string;
  }): DynamicBlocks[] => {
    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: previousComment || '',
        date: previousCommentDate || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const textareaBlock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'clarification.form.comment',
        placeholder: 'clarification.form.comment_placeholder',
        value: comment.getValue(),
        onChange: (value: string) => onCommentChange(value),
        rows: 5,
        error: error,
      },
    };

    const validateButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.validate',
        onClick: () => validate(),
        loading: loadingValidation,
        severity: 'success',
      },
    };

    const rejectButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.reject',
        onClick: () => reject(),
        loading: loadingValidation,
        severity: 'danger',
      },
    };

    const cancelButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.cancel',
        onClick: () => cancel(),
        severity: 'secondary',
      },
    };

    const finalBlocks: DynamicBlocks = {};
    if (previousComment) finalBlocks['0'] = [commentBlock];
    finalBlocks['1'] = [validateButton, rejectButton, cancelButton];
    finalBlocks['2'] = [textareaBlock];

    return [finalBlocks];
  };

  getAffectationCsBlocks = ({
    affect,
    reject,
    cancel,
    onCommentChange,
    comment,
    previousComment,
    previousCommentDate,
    loadingValidation,
    error,
  }: {
    affect: () => void;
    reject: () => void;
    cancel: () => void;
    onCommentChange: (comment: string) => void;
    comment: BehaviorSubject<string>;
    previousComment?: string;
    previousCommentDate?: string;
    loadingValidation: BehaviorSubject<boolean>;
    error?: BehaviorSubject<string | null>;
  }): DynamicBlocks[] => {
    const blocks: DynamicBlock[] = [
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.affect_rapporteur',
          onClick: () => affect(),
          severity: 'success',
          loading: loadingValidation,
        },
      },
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.reject',
          onClick: () => reject(),
          severity: 'danger',
          loading: loadingValidation,
        },
      },
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.cancel',
          onClick: () => cancel(),
          severity: 'secondary',
        },
      },
    ];

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: previousComment || '',
        date: previousCommentDate || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const textareaBlock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'clarification.form.comment',
        placeholder: 'clarification.form.comment_placeholder',
        value: comment.getValue(),
        onChange: (value: string) => onCommentChange(value),
        rows: 5,
        error: error,
      },
    };

    const finalBlocks: DynamicBlocks = {};
    if (previousComment) finalBlocks['0'] = [commentBlock];
    finalBlocks['1'] = blocks;
    finalBlocks['2'] = [textareaBlock];

    return [finalBlocks];
  };

  getNoteRapporteurCsBlocks = ({
    validate,
    previousComment,
    previousCommentDate,
  }: {
    validate: () => void;
    previousComment?: string;
    previousCommentDate?: string;
  }): DynamicBlocks[] => {
    const validateButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.passer_pour_validation',
        onClick: () => validate(),
        severity: 'success',
      },
    };

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: previousComment || '',
        date: previousCommentDate || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const finalBlocks: DynamicBlocks = {};
    if (previousComment) finalBlocks['0'] = [commentBlock];
    finalBlocks['1'] = [validateButton];

    return [finalBlocks];
  };

  getCncmpFilterBlocks = ({
    validate,
    complement,
    cancel,
    onCommentChange,
    comment,
    previousComment,
    previousCommentDate,
    loadingValidation,
    error,
  }: {
    validate: () => void;
    complement: () => void;
    cancel: () => void;
    onCommentChange: (comment: string) => void;
    comment: BehaviorSubject<string>;
    previousComment?: string;
    previousCommentDate?: string;
    loadingValidation: BehaviorSubject<boolean>;
    error?: BehaviorSubject<string | null>;
  }): DynamicBlocks[] => {
    const blocks: DynamicBlock[] = [
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.validate',
          onClick: () => validate(),
          severity: 'success',
          loading: loadingValidation,
        },
      },
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.complement_information',
          onClick: () => complement(),
          severity: 'danger',
          loading: loadingValidation,
        },
      },
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.cancel',
          onClick: () => cancel(),
          severity: 'secondary',
        },
      },
    ];

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: previousComment || '',
        date: previousCommentDate || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const textareaBlock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'clarification.form.comment',
        placeholder: 'clarification.form.comment_placeholder',
        value: comment.getValue(),
        onChange: (value: string) => onCommentChange(value),
        rows: 5,
        error: error,
      },
    };

    const finalBlocks: DynamicBlocks = {};
    if (previousComment) finalBlocks['0'] = [commentBlock];
    finalBlocks['1'] = blocks;
    finalBlocks['2'] = [textareaBlock];

    return [finalBlocks];
  };

  getValidateurAcBlocks = ({
    validate,
    reject,
    cancel,
    downloadModel,
    generateDemandeModelsPdf,
    onCommentChange,
    comment,
    previousComment,
    previousCommentDate,
    loadingValidation,
    error,
  }: {
    validate: () => void;
    reject: () => void;
    cancel: () => void;
    downloadModel: () => void;
    generateDemandeModelsPdf: () => void;
    onCommentChange: (comment: string) => void;
    comment: BehaviorSubject<string>;
    previousComment?: string;
    previousCommentDate?: string;
    loadingValidation: BehaviorSubject<boolean>;
    error?: BehaviorSubject<string | null>;
  }): DynamicBlocks[] => {
    const blocks: DynamicBlock[] = [
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.validate',
          onClick: () => validate(),
          severity: 'success',
          loading: loadingValidation,
        },
      },
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.reject',
          onClick: () => reject(),
          severity: 'danger',
          loading: loadingValidation,
        },
      },
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.download_model',
          onClick: () => downloadModel(),
          severity: 'info',
          icon: 'pi pi-download',
        },
      },
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.download_model_pdf',
          onClick: () => generateDemandeModelsPdf(),
          severity: 'info',
          icon: 'pi pi-download',
        },
      },
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.cancel',
          onClick: () => cancel(),
          severity: 'secondary',
        },
      },
    ];

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: previousComment || '',
        date: previousCommentDate || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const textareaBlock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'clarification.form.comment',
        placeholder: 'clarification.form.comment_placeholder',
        value: comment.getValue(),
        onChange: (value: string) => onCommentChange(value),
        rows: 5,
        error: error,
      },
    };

    const finalBlocks: DynamicBlocks = {};
    if (previousComment) finalBlocks['0'] = [commentBlock];
    finalBlocks['1'] = blocks;
    finalBlocks['2'] = [textareaBlock];

    return [finalBlocks];
  };

  getDecisionCncmpBlocks = ({
    validate,
    reject,
    cancel,
    onCommentChange,
    comment,
    previousComment,
    previousCommentDate,
    loadingValidation,
    error,
  }: {
    validate: () => void;
    reject: () => void;
    cancel: () => void;
    onCommentChange: (comment: string) => void;
    comment: BehaviorSubject<string>;
    previousComment?: string;
    previousCommentDate?: string;
    loadingValidation: BehaviorSubject<boolean>;
    error?: BehaviorSubject<string | null>;
  }): DynamicBlocks[] => {
    const blocks: DynamicBlock[] = [
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.validate',
          onClick: () => validate(),
          severity: 'success',
          loading: loadingValidation,
        },
      },
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'Rejeter',
          onClick: () => reject(),
          severity: 'danger',
          loading: loadingValidation,
        },
      },
      {
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.cancel',
          onClick: () => cancel(),
          severity: 'secondary',
        },
      },
    ];

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: previousComment || '',
        date: previousCommentDate || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const textareaBlock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'clarification.form.comment',
        placeholder: 'clarification.form.comment_placeholder',
        value: comment.getValue(),
        onChange: (value: string) => onCommentChange(value),
        rows: 5,
        error,
      },
    };

    const finalBlocks: DynamicBlocks = {};
    if (previousComment) finalBlocks['0'] = [commentBlock];
    finalBlocks['1'] = blocks;
    finalBlocks['2'] = [textareaBlock];

    return [finalBlocks];
  };

  getRedacteurGroupBlocks = ({
    saveAnswers,
    createDemande,
    cancel,
    loading,
  }: {
    saveAnswers: () => void;
    createDemande: () => void;
    cancel: () => void;
    loading: BehaviorSubject<boolean>;
  }): DynamicBlocks[] => {
    const saveButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.save_answers',
        onClick: () => saveAnswers(),
        loading,
        severity: 'success',
      },
    };

    const createDemandeButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.create_demande',
        onClick: () => createDemande(),
        loading,
        severity: 'info',
      },
    };

    const cancelButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.cancel',
        onClick: () => cancel(),
        severity: 'secondary',
      },
    };

    const finalBlocks: DynamicBlocks = {};
    finalBlocks['1'] = [saveButton, createDemandeButton, cancelButton];

    return [finalBlocks];
  };

  getStartDemandeBlocks = ({
    start,
    cancel,
    loading,
    generateModels,
    uploadFile,
    startDisabled = of(false),
  }: {
    start: () => void;
    cancel: () => void;
    loading: BehaviorSubject<boolean>;
    generateModels?: () => void;
    uploadFile?: () => void;
    startDisabled?: Observable<boolean>;
  }): DynamicBlocks[] => {
    if (!this.authService.hasRole(UserRole.REDACTEUR_AC)) {
      return [];
    }

    const startButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.passer_pour_validation',
        onClick: () => start(),
        loading,
        disabled: startDisabled,
        severity: 'success',
      },
    };

    const downloadButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.download_model',
        onClick: () => generateModels?.(),
        severity: 'info',
        icon: 'pi pi-download',
      },
    };

    const uploadButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.upload_signed_doc',
        onClick: () => uploadFile?.(),
        severity: 'info',
        icon: 'pi pi-upload',
      },
    };

    const cancelButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.cancel',
        onClick: () => cancel(),
        severity: 'secondary',
      },
    };

    const finalBlocks: DynamicBlocks = {};
    const actions: any[] = [startButton];
    if (generateModels) actions.push(downloadButton);
    if (uploadFile) actions.push(uploadButton);
    actions.push(cancelButton);
    finalBlocks['1'] = actions;

    return [finalBlocks];
  };

  getFlowableDemandeBlocks = ({
    demandeWithTask,
    validate,
    reject,
    cancel,
    comment,
    onCommentChange,
    error,
    downloadWord,
    uploadWord,
  }: {
    demandeWithTask: any;
    validate: (value: string, label?: string) => void;
    reject: () => void;
    cancel: () => void;
    comment: BehaviorSubject<string>;
    onCommentChange: (value: string) => void;
    error: BehaviorSubject<string | null>;
    downloadWord?: () => void;
    uploadWord?: () => void;
  }): DynamicBlocks[] => {
    const buttons: DynamicBlock[] = [];
    let renderTextarea = true;

    if (demandeWithTask?.nextSteps) {
      const seen = new Set();
      demandeWithTask.nextSteps
        .sort((a: any, b: any) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach((step: any) => {
          const isValidate = step.validationValue === 'true' || (demandeWithTask.nextSteps.length === 1 && step.validationValue === null);
          const val = step.validationValue === null && demandeWithTask.nextSteps.length === 1 ? 'true' : step.validationValue;
          const label = (step.actionLabel === 'Passer pour validation' && !isValidate) ? 'Rejeter' : (step.actionLabel || 'Valider');

          if (seen.has(label)) return;
          seen.add(label);

          buttons.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: label,
              onClick: () => {
                if (step.validationVariable || (demandeWithTask.nextSteps.length === 1 && !step.validationVariable)) {
                  validate(val, label);
                } else {
                  reject();
                }
              },
              severity: isValidate ? 'success' : 'danger',
            },
          });
        });
    }

    if (downloadWord) {
      buttons.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.download_model',
          onClick: () => downloadWord(),
          severity: 'info',
          icon: 'pi pi-download',
        },
      });
    }

    if (uploadWord) {
      buttons.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: 'clarification.actions.upload_signed_doc',
          onClick: () => uploadWord(),
          severity: 'info',
          icon: 'pi pi-upload',
        },
      });
    }

    buttons.push({
      variant: BlockVariant.BUTTON,
      props: {
        label: 'clarification.actions.cancel',
        onClick: () => cancel(),
        severity: 'secondary',
      },
    });

    const textareaBlock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'clarification.form.comment',
        placeholder: 'clarification.form.comment_placeholder',
        value: comment.getValue(),
        onChange: (value: string) => onCommentChange(value),
        rows: 5,
        error: error,
      },
    };

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: demandeWithTask?.traceability?.comment || '',
        date: demandeWithTask?.traceability?.dateComment || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const taskDef = demandeWithTask?.taskDefinitionKey;
    const isPublicationTask = taskDef === 'publicationTask';

    const finalBlocks: DynamicBlocks = {};
    if (demandeWithTask?.traceability?.comment) finalBlocks['0'] = [commentBlock];
    finalBlocks['1'] = buttons;
    if (!isPublicationTask) {
      finalBlocks['2'] = [textareaBlock];
    }

    return [finalBlocks];
  };
}
